import {
  type CountryCode,
  DEFAULT_COUNTRY_CODES,
} from '@repo/design-system-kit/constants/countries';

const ONE_YEAR_IN_MILLISECONDS = 31_536_000_000;
const DATE_STRING_REGEX = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

type FormatMessage = (descriptor: { id: string }) => string;

interface ActivityCopyOptions {
  formatMessage: FormatMessage;
  isAssociation: boolean;
  isFoundation: boolean;
  showBeneficiariesType: boolean;
}

export function getRecommendedCountries(legalCountry: string | null | undefined): CountryCode[] {
  const country = DEFAULT_COUNTRY_CODES.find(code => code === legalCountry);

  return country ? [country] : [];
}

export function formatTitle({
  formatMessage,
  isAssociation,
  isFoundation,
  showBeneficiariesType,
}: ActivityCopyOptions): string {
  if (showBeneficiariesType) {
    return formatMessage({ id: 'customer-registration-kit.organization-activity.title' });
  }

  if (isFoundation) {
    return formatMessage({
      id: 'customer-registration-kit.organization-activity.foundation.title',
    });
  }

  if (isAssociation) {
    return formatMessage({
      id: 'customer-registration-kit.organization-activity.association.title',
    });
  }

  return formatMessage({ id: 'customer-registration-kit.organization-activity.business.title' });
}

export function formatFrenchSubtitle({ formatMessage }: { formatMessage: FormatMessage }): string {
  return formatMessage({ id: 'customer-registration-kit.organization-activity.fr.subtitle' });
}

export function formatSubtitle({
  formatMessage,
  isAssociation,
  isFoundation,
  showBeneficiariesType,
}: ActivityCopyOptions): string {
  if (showBeneficiariesType) {
    return formatMessage({ id: 'customer-registration-kit.organization-activity.subtitle' });
  }

  if (isFoundation) {
    return formatMessage({
      id: 'customer-registration-kit.organization-activity.foundation.subtitle',
    });
  }

  if (isAssociation) {
    return formatMessage({
      id: 'customer-registration-kit.organization-activity.association.subtitle',
    });
  }

  return formatMessage({ id: 'customer-registration-kit.organization-activity.business.subtitle' });
}

export function formatIncomingTransfersLabel({
  formatMessage,
  showBeneficiariesType,
}: Pick<ActivityCopyOptions, 'formatMessage' | 'showBeneficiariesType'>): string {
  if (showBeneficiariesType) {
    return formatMessage({
      id: 'customer-registration-kit.organization-activity.countries-money-in.label',
    });
  }

  return formatMessage({
    id: 'customer-registration-kit.business-activity.countries-of-operations-money-in.label',
  });
}

export function formatOutgoingTransfersLabel({
  formatMessage,
  showBeneficiariesType,
}: Pick<ActivityCopyOptions, 'formatMessage' | 'showBeneficiariesType'>): string {
  if (showBeneficiariesType) {
    return formatMessage({
      id: 'customer-registration-kit.organization-activity.countries-money-out.label',
    });
  }

  return formatMessage({
    id: 'customer-registration-kit.business-activity.countries-of-operations-money-out.label',
  });
}

export function isOlderThanOrEqualOneYear(date: Date | string): boolean {
  const inputDate = normalizeDate(date);
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  return (currentDate.getTime() - inputDate.getTime()) / ONE_YEAR_IN_MILLISECONDS >= 1;
}

export function shouldShowBusinessRelationships(legalRegistrationDate?: Date | string | null) {
  if (!legalRegistrationDate) {
    return true;
  }

  return isOlderThanOrEqualOneYear(legalRegistrationDate);
}

function normalizeDate(date: Date | string): Date {
  if (date instanceof Date) {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    return normalizedDate;
  }

  if (!DATE_STRING_REGEX.test(date)) {
    throw new Error(
      `Util function isOlderThanOrEqualOneYear only supports Date objects or string dates with the format yyyy-mm-dd. This input date ${date} is not supported.`
    );
  }

  return new Date(`${date}T00:00:00`);
}
